import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import Select from 'react-select'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { KTIcon } from '../../../../_metronic/helpers'
import { buildDropdownAudit, fetchData, formatNumber, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { PlacementOption, SelectOption, StepCommonProps } from '../../types/ads'

interface Props extends StepCommonProps {
  placements: PlacementOption[]
  vendorList: SelectOption[]
  goToNext: () => void
}

const placementSchema = Yup.object().shape({
  vendorId: Yup.string().required('Please Select A Vendor'),
  adssettingId: Yup.number().nullable().required('Please Select A Placement'),
})

const PlacementStep: FC<Props> = ({ t, adsId, adminId, adData, placements, vendorList, onSaveSuccess, goToNext }) => {
  const safeList = useMemo(() => Array.isArray(placements) ? placements : [], [placements])
  const safeVendors = useMemo(() => Array.isArray(vendorList) ? vendorList : [], [vendorList])

  const [selectedVendor, setSelectedVendor] = useState<SelectOption | null>(null)
  const [saving, setSaving] = useState(false)

  const formik = useFormik({
    initialValues: {
      vendorId: '',
      adssettingId: null as number | null,
    },
    validationSchema: placementSchema,
    onSubmit: async (values) => {
      setSaving(true)
      try {
        const completeSteps = { ...(adData?.completeSteps ?? {}), placement: true }

        const oldPlacement = safeList.find((p) => p.adssettingId === adData?.adssettingId)
        const newPlacement = safeList.find((p) => p.adssettingId === values.adssettingId)

        const param: Record<string, any> = {
          adminId,
          vendorId: values.vendorId,
          adssettingId: values.adssettingId,
          adsId: adsId || 0,
          completeSteps,
        }

        if (adsId > 0) {
          param.labels = buildDropdownAudit({
            adssettingId: {
              old: { value: adData?.adssettingId ?? null, label: oldPlacement?.placementName ?? '' },
              new: { value: values.adssettingId, label: newPlacement?.placementName ?? '' },
            },
          })
        }

        const response = await fetchData('admin/ads/placement/save', param)
        if (String(response?.status) === '200') {
          toastsuccessmsg(response?.message || 'Placement Saved')
          onSaveSuccess(response?.data ?? {})
          goToNext()
        } else {
          toasterrormsg(response?.message || 'Failed To Save Placement')
        }
      } catch (error) {
        console.error('Placement save error:', error)
        toasterrormsg('Failed To Save Placement')
      } finally {
        setSaving(false)
      }
    },
  })

  // Pre-fill from adData on edit
  useEffect(() => {
    if (adData?.adssettingId) formik.setFieldValue('adssettingId', adData.adssettingId)
    if (adData?.vendorId) formik.setFieldValue('vendorId', String(adData.vendorId))
    if (adData?.vendorId && safeVendors.length > 0) {
      const found = safeVendors.find((v) => String(v.value) === String(adData.vendorId))
      if (found) setSelectedVendor(found)
    }
  }, [adData, safeVendors.length])

  const handleVendorChange = useCallback((opt: any) => {
    setSelectedVendor(opt)
    formik.setFieldValue('vendorId', opt?.value ? String(opt.value) : '')
    formik.setFieldTouched('vendorId', true)
  }, [formik])

  const handlePlacementSelect = useCallback((id: number) => {
    formik.setFieldValue('adssettingId', id)
    formik.setFieldTouched('adssettingId', true)
  }, [formik])

  // Filter placements: show if categoryId matches OR if it's the already-selected placement
  const filteredPlacements = useMemo(() => {
    const detailCategoryId = adData?.categoryId ?? 0
    const selectedSettingId = adData?.adssettingId ?? 0
    return safeList.filter((p) => {
      if (p.adssettingId === selectedSettingId) return true
      const cats = Array.isArray(p.categoryId) ? p.categoryId : []
      return detailCategoryId > 0 && cats.includes(detailCategoryId)
    })
  }, [safeList, adData?.categoryId, adData?.adssettingId])

  return (
    <>
      <div className='mb-6'>
        <h4 className='fw-bold fs-4 mb-1'>{t('Select Ad Placement')}</h4>
        <p className='text-muted fs-7 mb-0'>{t('Choose vendor and where the ad should appear')}</p>
      </div>

      {/* Vendor Select */}
      <div className='mb-6'>
        <label className='adform-label'>{t('Vendor')} <span className='text-danger'>*</span></label>
        <Select
          placeholder={t('Search and select vendor...')}
          isDisabled={true}
          isSearchable
          options={safeVendors}
          value={selectedVendor}
          onChange={(opt: any) => handleVendorChange(opt)}
          classNamePrefix='adform-select'
        />
      </div>

      {/* Placements */}
      {filteredPlacements.length === 0 ? (
        <div className='text-center py-10'>
          <KTIcon iconName='information-5' className='fs-3x text-muted mb-3' />
          <p className='text-muted fs-6'>{t('No placements available.')}</p>
        </div>
      ) : (
        <div className='row g-4'>
          {filteredPlacements.map((p) => {
            if (!p?.adssettingId) return null
            const isSelected = formik.values.adssettingId === p.adssettingId
            return (
              <div className='col-12 col-md-6' key={p.adssettingId}>
                <div
                  className={`adform-placement-card ${isSelected ? 'adform-placement-card--selected' : ''}`}
                  onClick={() => handlePlacementSelect(p.adssettingId)}
                >
                  <div className='adform-placement-card__body'>
                    <div className='adform-placement-card__info'>
                      <div className='fw-bold fs-5'>{p?.placementName || '-'}</div>
                      <p className='text-muted fs-7 mb-2'>{p?.description || ''}</p>
                      <span className='text-muted fs-8'>{p?.maxAds ?? 0} {t('Ad Slots')}</span>
                    </div>
                    <div className='adform-placement-card__price'>
                      <span className='fw-bolder fs-4'>{formatNumber(p?.amountPerDay ?? 0 , false)}</span>
                      <span className='text-muted fs-8 px-1'>{t('UZS/day')}</span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className='adform-placement-card__check'>
                      <KTIcon iconName='check-circle' className='fs-1 text-primary' iconType='solid' />
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {formik.touched.vendorId && formik.errors.vendorId && (
        <div className='text-danger fs-7 mt-3'>{formik.errors.vendorId}</div>
      )}
      {formik.touched.adssettingId && formik.errors.adssettingId && (
        <div className='text-danger fs-7 mt-3'>{formik.errors.adssettingId}</div>
      )}

      {/* Save & Next */}
      <div className='d-flex justify-content-end mt-6'>
        <button
          type='button'
          className='btn btn-primary d-flex align-items-center gap-2 px-5 py-3'
          onClick={() => formik.handleSubmit()}
          disabled={saving}
        >
          {saving ? <span className='spinner-border spinner-border-sm' /> : <KTIcon iconName='check' className='fs-3' />}
          <span>{t('Save & Next')}</span>
          <KTIcon iconName='arrow-right' className='fs-3' />
        </button>
      </div>
    </>
  )
}

export { PlacementStep }
